import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../../use-stores";
import {
  Box,
  Dialog,
  IconButton,
  Radio,
  Checkbox,
  Chip,
  Card,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AppTableHeaderCell from "../../common/pages/AppTableHeaderCell";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../../styles/Shipmentstyles";
import {
  Close as CloseIcon,
  // Sync as SyncIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Inventory2 as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryBoxIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import CustomTypography from "../../common/form/CustomTypography";
import AppButton from "../../common/buttons/AppButton";
import AppDropDown from "../../common/filter/AppDropDown";
import FormTextField from "../../common/form/FormTextField";
// import my_inventory_icon from "../../../../assets/swagstore/my_inventory.png";
import catalogue_icon from "../../../../assets/swagstore/catalogue.png";
import upload_csv_template_icon from "../../../../assets/swagstore/upload_csvtemplate.png";
import ImageUpload from "../../common/upload/ImageUpload";
import { useNotification } from "../../../../utils/NotificationsContext";
import { getPackDisplayImage } from "../../../../utils/packImageUtils";
// import { FilterValue } from "../../../../models/Filters/FilterValue";
import { SortingOptions } from "../../../../models/Filters/FilterOptions";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";

interface AddProductsModalProps {
  open: boolean;
  onClose: () => void;
  onAddProducts: (csvFile?: File | null) => void;
}

const AddProductsModal: React.FC<AddProductsModalProps> = observer(
  ({ open, onClose, onAddProducts }) => {
    const { swagStoreStore, userStore } = useStores();
    const theme = useTheme();
    const { showError } = useNotification();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [csvFile, setCsvFile] = React.useState<File | null>(null);
    const [expandedPackIds, setExpandedPackIds] = React.useState<Set<number>>(
      new Set(),
    );
    const [expandedProductIds, setExpandedProductIds] = React.useState<
      Set<number>
    >(new Set());

    // Memoize category values list - must be at top level (Rules of Hooks)
    const categoryValuesList = useMemo(
      () =>
        swagStoreStore.selectedCatalogueCategoryIds
          .filter(
            (id) => id != null && id !== undefined && !isNaN(id) && id > 0,
          )
          .map((id) => String(id)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [swagStoreStore.selectedCatalogueCategoryIds.join(",")],
    );

    // Memoize category list - must be at top level (Rules of Hooks)
    const categoryList = useMemo(
      () =>
        swagStoreStore.categories.map((cat) => ({
          id: String(cat.category_id),
          value: cat.category_name,
        })),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [swagStoreStore.categories],
    );

    const handleToggleInventoryProduct = (productId: number) => {
      if (
        !userStore.hasPermission(EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE)
      )
        return;
      swagStoreStore.toggleInventoryProduct(productId);
    };

    const handleToggleCatalogueProduct = (productId: number) => {
      if (
        !userStore.hasPermission(EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE)
      )
        return;
      swagStoreStore.toggleCatalogueProduct(productId);
    };

    const handleSelectAllInventory = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();
      if (
        !userStore.hasPermission(EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE)
      )
        return;
      const allProductIds = swagStoreStore.fetchedInventoryProducts.map(
        (pack) => pack.id,
      );
      if (allProductIds.length > 0) {
        swagStoreStore.selectAllInventoryProducts(allProductIds);
      }
    };

    const handleSelectAllCatalogue = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();
      if (
        !userStore.hasPermission(EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE)
      )
        return;
      const allProductIds = swagStoreStore.filteredCatalogueProducts.map(
        (pack) => pack.id,
      );
      if (allProductIds.length > 0) {
        swagStoreStore.selectAllCatalogueProducts(allProductIds);
      }
    };

    const handleAddSelectedProducts = () => {
      const fileToUpload =
        swagStoreStore.selectedSource === "csv" ? csvFile : null;
      onAddProducts(fileToUpload);
    };

    const handleCsvFileSelect = (file: File) => {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        alert("Please upload a CSV file");
        return;
      }
      setCsvFile(file);
    };

    const handleDownloadTemplate = (type: "inventory" | "catalogue") => {
      if (type === "catalogue") {
        if (!swagStoreStore.storeId) {
          return;
        }
        swagStoreStore
          .downloadCatalogueTemplate()
          .then((response) => {
            if (response.data) {
              const downloadUrl = window.URL.createObjectURL(response.data);
              const a = document.createElement("a");
              a.href = downloadUrl;
              a.download = response.filename
                ? response.filename
                : `Catalogue.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(downloadUrl);
            } else {
              showError(
                "Error",

                "Failed to download the Catalogue template csv file.",
              );
            }
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to download the Catalogue template csv file.",
            );
          });
      }
    };

    const handleClose = () => {
      swagStoreStore.clearSelectedProducts();
      setCsvFile(null);
      swagStoreStore.setSelectedSource("inventory");
      onClose();
    };

    const getSelectedProductsCount = () => {
      if (swagStoreStore.selectedSource === "inventory") {
        return swagStoreStore.selectedInventoryProducts.length;
      } else if (swagStoreStore.selectedSource === "catalogue") {
        return swagStoreStore.selectedCatalogueProducts.length;
      } else if (swagStoreStore.selectedSource === "csv") {
        return csvFile ? 1 : 0;
      }
      return 0;
    };

    // Auto-select items with display_on_store = true when modal opens or products are fetched
    useEffect(() => {
      if (open && swagStoreStore.modalOpen) {
        swagStoreStore.initializeSelectedProducts();
        // Load categories if not already loaded
        if (swagStoreStore.categories.length === 0) {
          swagStoreStore.getCategories().catch(console.error);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      open,
      swagStoreStore.modalOpen,
      swagStoreStore.fetchedInventoryProducts,
      swagStoreStore.fetchedCatalogueProducts,
    ]);

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxHeight: isMobile ? "90vh" : "85vh",
            margin: isMobile ? "16px" : "32px",
            "@media (min-width: 1200px)": {
              width: "1050px",
              maxWidth: "none",
            },
            minHeight: "95vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor:
                theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            p: 3,
            pb: 1,
          }}
        >
          <Box>
            <CustomTypography
              variant="heading4"
              fontWeight={600}
              style={{ marginBottom: "8px" }}
            >
              Add products to catalogue
            </CustomTypography>
            <CustomTypography
              variant="heading6"
              color={theme.palette.text.secondary}
              fontWeight={400}
            >
              Choose how you want to add new products to your store
            </CustomTypography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action?.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 3,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Source Selection Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              mb: 4,
            }}
          >
            {/* From My Inventory */}
            <Card
              onClick={() => {
                swagStoreStore.setSelectedSource("inventory");
                swagStoreStore.setInventoryPage(1);
              }}
              sx={{
                flex: 1,
                p: 2,
                cursor: "pointer",
                border:
                  swagStoreStore.selectedSource === "inventory"
                    ? `2px solid ${theme.palette.customColors?.purple}`
                    : `1px solid ${theme.palette.customColors.lightGray}`,
                borderRadius: "12px",

                "&:hover": {
                  borderColor: theme.palette.customColors?.purple,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // justifyContent: "center",
                      gap: 3,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <img
                        src={upload_csv_template_icon}
                        alt="Swageazy Catalogue"
                      />
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        style={{ marginBottom: "6px" }}
                      >
                        From My Inventory
                      </CustomTypography>
                      <CustomTypography
                        variant="body3"
                        color={theme.palette.text.secondary}
                      >
                        Add products from your company's inventory
                      </CustomTypography>
                    </Box>
                  </Box>
                </Box>
                <Radio
                  checked={swagStoreStore.selectedSource === "inventory"}
                  value="inventory"
                  sx={{
                    color: theme.palette.customColors?.lightGray,
                    "&.Mui-checked": {
                      color: theme.palette.customColors?.purple,
                    },
                    padding: "4px",
                    marginTop: "-15px",
                    marginRight: "-15px",
                  }}
                />
              </Box>
            </Card>

            {/* From Swageazy Catalogue */}
            <Card
              onClick={() => {
                swagStoreStore.setSelectedSource("catalogue");
                // Reset to first page when switching to catalogue
                swagStoreStore.setCataloguePage(1);
              }}
              sx={{
                flex: 1,
                p: 2,
                cursor: "pointer",
                border:
                  swagStoreStore.selectedSource === "catalogue"
                    ? `2px solid ${theme.palette.customColors?.purple}`
                    : `1px solid ${theme.palette.customColors.lightGray}`,
                borderRadius: "12px",

                "&:hover": {
                  borderColor: theme.palette.customColors?.purple,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // justifyContent: "center",
                      gap: 3,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <img src={catalogue_icon} alt="Swageazy Catalogue" />
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        style={{ marginBottom: "6px" }}
                      >
                        From Swageazy Catalogue
                      </CustomTypography>
                      <CustomTypography
                        variant="body3"
                        color={theme.palette.text.secondary}
                      >
                        Browse our curated product catalogue
                      </CustomTypography>
                    </Box>
                  </Box>
                </Box>
                <Radio
                  checked={swagStoreStore.selectedSource === "catalogue"}
                  value="catalogue"
                  sx={{
                    color: theme.palette.customColors?.lightGray,
                    "&.Mui-checked": {
                      color: theme.palette.customColors?.purple,
                    },
                    padding: "4px",
                    marginTop: "-15px",
                    marginRight: "-15px",
                  }}
                />
              </Box>
            </Card>

            {/* Upload via CSV Template */}
            {/* <Card
              onClick={() => swagStoreStore.setSelectedSource("csv")}
              sx={{
                flex: 1,
                p: 2,
                cursor: "pointer",
                border:
                  swagStoreStore.selectedSource === "csv"
                    ? `2px solid ${theme.palette.customColors?.purple}`
                    : `1px solid ${theme.palette.customColors.lightGray}`,
                borderRadius: "12px",

                "&:hover": {
                  borderColor: theme.palette.customColors?.purple,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <img src={my_inventory_icon} alt="CSV Template" />
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        style={{ marginBottom: "6px" }}
                      >
                        Upload via CSV Template
                      </CustomTypography>
                      <CustomTypography
                        variant="body3"
                        color={theme.palette.text.secondary}
                      >
                        Import products using <br /> CSV file
                      </CustomTypography>
                    </Box>
                  </Box>
                </Box>
                <Radio
                  checked={swagStoreStore.selectedSource === "csv"}
                  value="csv"
                  sx={{
                    color: theme.palette.customColors?.lightGray,
                    "&.Mui-checked": {
                      color: theme.palette.customColors?.purple,
                    },
                    padding: "4px",
                    marginTop: "-15px",
                    marginRight: "-15px",
                  }}
                />
              </Box>
            </Card> */}
          </Box>

          <Divider />

          {/* My Inventory Section */}
          {swagStoreStore.selectedSource === "inventory" && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 2,
                  mt: 4,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {(() => {
                    const allProductIds =
                      swagStoreStore.fetchedInventoryProducts.map(
                        (pack) => pack.id,
                      );
                    const allSelected =
                      allProductIds.length > 0 &&
                      allProductIds.every((id) =>
                        swagStoreStore.selectedInventoryProducts.includes(id),
                      );
                    const someSelected =
                      allProductIds.length > 0 &&
                      allProductIds.some((id) =>
                        swagStoreStore.selectedInventoryProducts.includes(id),
                      ) &&
                      !allSelected;

                    return (
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={
                          !userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                        }
                        onClick={(event) => handleSelectAllInventory(event)}
                        sx={{
                          color: theme.palette.customColors?.lightGrayBorder,
                          "&.Mui-checked": {
                            color: theme.palette.customColors?.blackBorder,
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: theme.palette.customColors?.blackBorder,
                          },
                        }}
                      />
                    );
                  })()}
                  <CustomTypography variant="heading6" fontWeight={700}>
                    My Inventory
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: isMobile ? "100%" : "250px", flex: 1 }}>
                    <FormTextField
                      placeholder="Search by order ID or label..."
                      value={swagStoreStore.inventorySearchTerm}
                      onChange={(e, value) => {
                        const searchValue = value || "";
                        swagStoreStore.setInventorySearchTerm(searchValue);

                        // If search field becomes empty, automatically fetch initial data
                        if (searchValue === "" || searchValue.trim() === "") {
                          swagStoreStore.setInventoryPage(1);
                          swagStoreStore.getPacks().catch(console.error);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          swagStoreStore.setInventoryPage(1);
                          swagStoreStore.getPacks().catch(console.error);
                        }
                      }}
                      sx={{
                        marginBottom: "0px",
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.customColors.grayBackGroundColor,
                        minHeight: "32px",
                        maxHeight: "32px",
                      }}
                      startAdornment={
                        <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                      }
                      borderColor={theme.palette.customColors.white}
                    />
                  </Box>

                  <Box sx={{ minWidth: isMobile ? "100%" : "200px" }}>
                    <AppDropDown
                      title="Sort by..."
                      isMultiSelect={false}
                      value={
                        swagStoreStore.inventorySortBy &&
                        swagStoreStore.inventorySortType !== null
                          ? `${swagStoreStore.inventorySortBy}_${swagStoreStore.inventorySortType}`
                          : ""
                      }
                      list={[
                        {
                          id: "quantity_0",
                          value: "Quantity (ASC)",
                        },
                        {
                          id: "quantity_1",
                          value: "Quantity (DESC)",
                        },
                        {
                          id: "label_0",
                          value: "Name (A-Z)",
                        },
                        {
                          id: "label_1",
                          value: "Name (Z-A)",
                        },
                      ]}
                      onSingleSelectionChange={(id, name) => {
                        if (!id || id === "") {
                          swagStoreStore.clearInventorySort();
                          swagStoreStore.setInventoryPage(1);
                          swagStoreStore.getPacks().catch(console.error);
                          return;
                        }

                        const lastUnderscoreIndex = id.lastIndexOf("_");
                        if (lastUnderscoreIndex === -1) {
                          return;
                        }

                        const sortBy = id.substring(0, lastUnderscoreIndex);
                        const sortTypeStr = id.substring(
                          lastUnderscoreIndex + 1,
                        );
                        const sortType = parseInt(
                          sortTypeStr,
                          10,
                        ) as SortingOptions;

                        if (
                          isNaN(sortType) ||
                          (sortType !== SortingOptions.ASC &&
                            sortType !== SortingOptions.DESC)
                        ) {
                          return;
                        }

                        swagStoreStore.setInventorySortBy(sortBy);
                        swagStoreStore.setInventorySortType(sortType);
                        swagStoreStore.setInventoryPage(1);

                        swagStoreStore.getPacks().catch((error) => {
                          console.error("Error calling getPacks:", error);
                        });
                      }}
                      fontSize="0.75rem"
                      borderColor={theme.palette.customColors.white}
                      borderRadius="8px"
                      backgroundColor={
                        theme.palette.customColors.grayBackGroundColor
                      }
                      height="32px"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Product Grid */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mb: 2,
                }}
              >
                {swagStoreStore.loadingInventory ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 8,
                      gap: 2,
                      minHeight: "300px",
                    }}
                  >
                    <CircularProgress
                      size={40}
                      sx={{
                        color: theme.palette.customColors?.purple,
                      }}
                    />
                    <CustomTypography
                      variant="body1"
                      color={theme.palette.text.secondary}
                    >
                      Loading inventory products...
                    </CustomTypography>
                  </Box>
                ) : swagStoreStore.fetchedInventoryProducts.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                      minHeight: "200px",
                    }}
                  >
                    <CustomTypography
                      variant="body1"
                      color={theme.palette.text.secondary}
                    >
                      No products found
                    </CustomTypography>
                  </Box>
                ) : (
                  swagStoreStore?.fetchedInventoryProducts?.map((pack) => {
                    const isSelected =
                      swagStoreStore.selectedInventoryProducts?.includes(
                        pack.id,
                      );
                    const packBasePriceFloor =
                      pack.pack_cost_price != null &&
                      !Number.isNaN(Number(pack.pack_cost_price))
                        ? Number(pack.pack_cost_price)
                        : undefined;
                    return (
                      <Card
                        key={pack.id}
                        sx={{
                          position: "relative",
                          p: 2,
                          cursor: userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                            ? "pointer"
                            : "default",
                          border: isSelected
                            ? `2px solid ${theme.palette.customColors?.purple}`
                            : `1px solid ${theme.palette.customColors.lightGray}`,
                          borderRadius: "12px",
                          backgroundColor: isSelected
                            ? theme.palette.customColors.blueLightBackground
                            : theme.palette.customColors.white,
                          "&:hover": userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                            ? {
                                borderColor: theme.palette.customColors?.purple,
                                backgroundColor: isSelected
                                  ? theme.palette.customColors
                                      .blueLightBackground
                                  : theme.palette.customColors
                                      .blueLightBackground,
                              }
                            : {},
                          transition: "all 0.2s ease",
                          opacity: userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                            ? 1
                            : 0.6,
                        }}
                        onClick={() => {
                          if (
                            userStore.hasPermission(
                              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                            )
                          ) {
                            setExpandedPackIds((prev) => {
                              const newSet = new Set(prev);
                              if (newSet.has(pack.id)) {
                                newSet.delete(pack.id);
                              } else {
                                newSet.add(pack.id);
                              }
                              return newSet;
                            });
                          }
                        }}
                      >
                        {/* Product Header Section - Horizontal Layout */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Checkbox - Left Side */}
                          <Box
                            sx={{
                              pt: 0.5,
                              marginLeft: "-8px",
                              marginRight: "-8px",
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                userStore.hasPermission(
                                  EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                )
                              ) {
                                handleToggleInventoryProduct(pack.id);
                              }
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: theme.palette.customColors?.purple,
                                padding: "4px",
                                "&.Mui-checked": {
                                  color: theme.palette.customColors?.purple,
                                },
                              }}
                            />
                          </Box>
                          {/* Product Image - Left Side */}
                          <Box
                            sx={{
                              width: "80px",
                              height: "80px",
                              minWidth: "80px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor:
                                theme.palette.customColors.veryLightGray,
                              borderRadius: "8px",
                              overflow: "hidden",
                              p: 1,
                            }}
                          >
                            <Avatar
                              src={getPackDisplayImage(pack)}
                              alt={pack.label}
                              variant="rounded"
                              sx={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "8px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>

                          {/* Product Info - Middle Section */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            {/* Product Name and Category */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <CustomTypography
                                variant="heading6"
                                fontWeight={600}
                                style={{ fontSize: "1rem" }}
                              >
                                {pack.label}
                              </CustomTypography>
                              {pack.category_name && (
                                <Chip
                                  label={pack.category_name}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      theme.palette.customColors
                                        .blueLightBackground,
                                    color: theme.palette.customColors?.purple,
                                    fontSize: "0.75rem",
                                    height: "24px",
                                  }}
                                />
                              )}
                            </Box>

                            {/* Order Details */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              {pack.order_id && (
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[700]}
                                  fontWeight={400}
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  Order ID: {pack.order_id}
                                </CustomTypography>
                              )}
                              {pack.order_id && (
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[700]}
                                  fontWeight={400}
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  •
                                </CustomTypography>
                              )}
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.grey[700]}
                                fontWeight={400}
                                style={{ fontSize: "0.8rem" }}
                              >
                                Pack ID: {pack.id}
                              </CustomTypography>
                              {pack.order_label && (
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[700]}
                                  fontWeight={400}
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  •
                                </CustomTypography>
                              )}
                              {pack.order_label && (
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[700]}
                                  fontWeight={400}
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  Order Label: {pack.order_label}
                                </CustomTypography>
                              )}
                            </Box>

                            {/* Summary Statistics */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <InventoryIcon
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: theme.palette.grey[600],
                                  }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[600]}
                                  fontWeight={400}
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  {pack.products?.reduce(
                                    (total, product) =>
                                      total + (product.variants?.length || 0),
                                    0,
                                  ) || 0}{" "}
                                  variants
                                </CustomTypography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TrendingUpIcon
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: theme.palette.grey[600],
                                  }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[600]}
                                  fontWeight={400}
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  {pack.products?.reduce((total, product) => {
                                    return (
                                      total +
                                      (product.variants?.reduce((sum, v) => {
                                        return sum + (v.quantity ?? 0);
                                      }, 0) ?? 0)
                                    );
                                  }, 0) || 0}{" "}
                                  in stock
                                </CustomTypography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Price and Selection - Right Side */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 1,
                              minWidth: "120px",
                              mt: 0.5,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Price */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.25,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <FormTextField
                                  type="number"
                                  disabled={
                                    !userStore.hasPermission(
                                      EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                    )
                                  }
                                  value={
                                    swagStoreStore.editedPackPrices[pack.id] !==
                                    undefined
                                      ? swagStoreStore.editedPackPrices[
                                          pack.id
                                        ] === null
                                        ? ""
                                        : (swagStoreStore.editedPackPrices[
                                            pack.id
                                          ] ?? "")
                                      : (pack.pack_price ?? "")
                                  }
                                  onChange={(e, value) => {
                                    if (
                                      !userStore.hasPermission(
                                        EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                      )
                                    )
                                      return;
                                    if (
                                      value === "" ||
                                      value === undefined ||
                                      value === null
                                    ) {
                                      swagStoreStore.setEditedPackPrice(
                                        pack.id,
                                        null,
                                      );
                                    } else {
                                      const price = Number(value);
                                      swagStoreStore.setEditedPackPrice(
                                        pack.id,
                                        isNaN(price) ? null : price,
                                      );
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (
                                      !userStore.hasPermission(
                                        EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                      ) ||
                                      packBasePriceFloor === undefined
                                    ) {
                                      return;
                                    }
                                    const raw = e.target.value;
                                    if (raw === "") return;
                                    const price = Number(raw);
                                    if (
                                      isNaN(price) ||
                                      price >= packBasePriceFloor
                                    ) {
                                      return;
                                    }
                                    swagStoreStore.setEditedPackPrice(
                                      pack.id,
                                      packBasePriceFloor,
                                    );
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  sx={{
                                    width: "80px",
                                    height: "32px",
                                    marginBottom: "0px",
                                    backgroundColor:
                                      theme.palette.customColors.lightGreyColor,
                                    "& .MuiInputBase-input": {
                                      paddingTop: "4px",
                                      paddingBottom: "4px",
                                      fontSize: "0.9rem",
                                      fontWeight: 600,
                                      textAlign: "left",
                                    },
                                    "& .MuiInputBase-root": {
                                      borderBottom: "none",
                                      "&::before": {
                                        borderBottom: "none",
                                      },
                                      "&::after": {
                                        borderBottom: "none",
                                      },
                                      "&:hover:not(.Mui-disabled):before": {
                                        borderBottom: "none",
                                      },
                                    },
                                  }}
                                  variant="standard"
                                  borderColor={theme.palette.customColors.white}
                                  startAdornment={
                                    <CurrencyRupeeIcon
                                      sx={{
                                        color: theme.palette.grey[500],
                                        height: "16px",
                                        width: "16px",
                                        marginLeft: "8px",
                                      }}
                                    />
                                  }
                                />
                                {/* Accordion Icon - Same line as price input */}
                                <IconButton
                                  size="small"
                                  sx={{
                                    padding: 0,
                                    color: theme.palette.grey[600],
                                    transform: expandedPackIds.has(pack.id)
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.3s ease",
                                  }}
                                >
                                  <ExpandMoreIcon />
                                </IconButton>
                              </Box>
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.grey[600]}
                                fontWeight={400}
                                sx={{ fontSize: "0.7rem", marginLeft: "8px" }}
                              >
                                Selling Price
                              </CustomTypography>
                            </Box>

                            {/* Units Selected */}
                            <Chip
                              label={`${
                                pack.products?.reduce((total, product) => {
                                  return (
                                    total +
                                    (product.variants?.reduce((sum, v) => {
                                      // Use original quantity_for_store (not edited values) - will update after API success
                                      return sum + (v.quantity_for_store ?? 0);
                                    }, 0) ?? 0)
                                  );
                                }, 0) || 0
                              } units selected`}
                              size="small"
                              sx={{
                                backgroundColor: "#E8F5E9",
                                color: "#2E7D32",
                                fontSize: "0.75rem",
                                height: "24px",
                                fontWeight: 500,
                                paddingLeft: "10px",
                                paddingRight: "10px",
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Variant Quantities Accordion */}
                        {pack.products?.some(
                          (product) =>
                            product.variants && product.variants.length > 0,
                        ) && (
                          <Accordion
                            expanded={expandedPackIds.has(pack.id)}
                            sx={{
                              mt: 1,
                              boxShadow: "none",
                              border: "none",
                              backgroundColor: "transparent",
                              "&:before": {
                                display: "none",
                              },
                              "&.Mui-expanded": {
                                margin: "8px 0",
                              },
                            }}
                          >
                            <AccordionSummary
                              sx={{
                                px: 0,
                                py: 0,
                                minHeight: "auto",
                                display: "none",
                              }}
                            />
                            <AccordionDetails
                              sx={{
                                px: 0,
                                pb: 0,
                                pt: 0,
                                backgroundColor: "transparent",
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.customColors.blackBorder}
                                fontWeight={600}
                                style={{
                                  fontSize: "0.8rem",
                                  marginBottom: "8px",
                                }}
                              >
                                Select Variants to Add
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.grey[600]}
                                fontWeight={400}
                                style={{
                                  fontSize: "0.8rem",
                                  marginBottom: "10px",
                                }}
                              >
                                Choose quantity for each variant you want to
                                list
                              </CustomTypography>
                              <TableContainer
                                component={Paper}
                                sx={{
                                  boxShadow: "none",
                                  border: `1px solid ${theme.palette.customColors.lightGray}`,
                                  borderRadius: "8px",
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        backgroundColor:
                                          theme.palette.customColors
                                            .cardBackground,
                                      }}
                                    >
                                      <AppTableHeaderCell
                                        title="SKU"
                                        fontSize="12px"
                                        fontColor={theme.palette.grey[700]}
                                        fontWeight={700}
                                        txtAlign="left"
                                        sx={{
                                          padding: "8px 12px",
                                          minWidth: "auto",
                                        }}
                                      />
                                      <AppTableHeaderCell
                                        title="SIZE"
                                        fontSize="12px"
                                        fontColor={theme.palette.grey[700]}
                                        fontWeight={700}
                                        txtAlign="left"
                                        sx={{
                                          padding: "8px 12px",
                                          minWidth: "auto",
                                        }}
                                      />
                                      <AppTableHeaderCell
                                        title="AVAILABLE"
                                        fontSize="12px"
                                        fontColor={theme.palette.grey[700]}
                                        fontWeight={700}
                                        txtAlign="left"
                                        sx={{
                                          padding: "8px 12px",
                                          minWidth: "auto",
                                        }}
                                      />
                                      <AppTableHeaderCell
                                        title="QTY ON STORE"
                                        fontSize="12px"
                                        fontColor={theme.palette.grey[700]}
                                        fontWeight={700}
                                        txtAlign="left"
                                        sx={{
                                          padding: "8px 12px",
                                          minWidth: "auto",
                                        }}
                                      />
                                      <AppTableHeaderCell
                                        title="QTY TO LIST"
                                        fontSize="12px"
                                        fontColor={theme.palette.grey[700]}
                                        fontWeight={700}
                                        txtAlign="right"
                                        sx={{
                                          padding: "8px 12px",
                                          minWidth: "auto",
                                        }}
                                      />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {pack.products?.map((product) => {
                                      if (
                                        !product.variants ||
                                        product.variants.length === 0
                                      ) {
                                        return null;
                                      }
                                      return product.variants.map((variant) => {
                                        const vid =
                                          variant.variant_id ?? variant.id;
                                        if (vid == null) return null;

                                        // Calculate available quantity: initial_qty - qty_for_store + used_qty
                                        // Use original quantity_for_store (not edited values) - will update after API success
                                        const currentQtyForStore =
                                          variant.quantity_for_store ?? 0;
                                        const availableQty =
                                          variant.quantity ?? 0;

                                        return (
                                          <StyledTableRow key={vid}>
                                            <StyledTableCell
                                              sx={{
                                                fontSize: "14px",
                                                padding: "8px 12px",
                                                fontWeight: 400,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <InventoryBoxIcon
                                                  sx={{
                                                    fontSize: "0.875rem",
                                                    color:
                                                      theme.palette.grey[600],
                                                  }}
                                                />
                                                {variant.sku ||
                                                  variant.client_sku ||
                                                  "-"}
                                              </Box>
                                            </StyledTableCell>
                                            <StyledTableCell
                                              sx={{
                                                fontSize: "14px",
                                                padding: "8px 12px",
                                                fontWeight: 400,
                                              }}
                                            >
                                              {variant.size || "-"}
                                            </StyledTableCell>
                                            <StyledTableCell
                                              sx={{
                                                padding: "8px 12px",
                                                fontWeight: 400,
                                              }}
                                            >
                                              <Chip
                                                label={`${
                                                  availableQty ?? 0
                                                } units`}
                                                size="small"
                                                sx={{
                                                  backgroundColor: "#E8F5E9",
                                                  color: "#2E7D32",
                                                  fontSize: "0.75rem",
                                                  height: "24px",
                                                  fontWeight: 500,
                                                  paddingLeft: "10px",
                                                  paddingRight: "10px",
                                                }}
                                              />
                                            </StyledTableCell>
                                            <StyledTableCell
                                              sx={{
                                                padding: "8px 12px",
                                                fontWeight: 400,
                                                textAlign: "left",
                                                fontSize: "0.875rem",
                                                paddingLeft: "2rem",
                                              }}
                                            >
                                              {currentQtyForStore}
                                            </StyledTableCell>
                                            <StyledTableCell
                                              sx={{
                                                padding: "8px 12px",
                                                fontWeight: 400,
                                                textAlign: "right",
                                              }}
                                              onMouseDown={(e) =>
                                                e.stopPropagation()
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              <Box
                                                onMouseDown={(e) =>
                                                  e.stopPropagation()
                                                }
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <FormTextField
                                                  type="number"
                                                  disabled={
                                                    !userStore.hasPermission(
                                                      EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                                    )
                                                  }
                                                  value={
                                                    swagStoreStore
                                                      .editedVariantQuantities[
                                                      pack.id
                                                    ]?.[vid] !== undefined
                                                      ? swagStoreStore
                                                          .editedVariantQuantities[
                                                          pack.id
                                                        ][vid] === null
                                                        ? ""
                                                        : (swagStoreStore
                                                            .editedVariantQuantities[
                                                            pack.id
                                                          ][vid] ?? "")
                                                      : ""
                                                  }
                                                  onChange={(e, value) => {
                                                    if (
                                                      !userStore.hasPermission(
                                                        EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                                      )
                                                    )
                                                      return;
                                                    if (
                                                      value === "" ||
                                                      value === undefined ||
                                                      value === null
                                                    ) {
                                                      swagStoreStore.setEditedVariantQuantity(
                                                        pack.id,
                                                        vid,
                                                        null,
                                                      );
                                                    } else {
                                                      const quantity =
                                                        Number(value);
                                                      swagStoreStore.setEditedVariantQuantity(
                                                        pack.id,
                                                        vid,
                                                        isNaN(quantity)
                                                          ? null
                                                          : quantity,
                                                      );
                                                    }
                                                  }}
                                                  onKeyDown={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  sx={{
                                                    width: "80px",
                                                    height: "28px",
                                                    marginBottom: "0px",
                                                    backgroundColor:
                                                      theme.palette.customColors
                                                        .lightGreyColor,
                                                    "& .MuiInputBase-input": {
                                                      paddingTop: "4px",
                                                      paddingBottom: "4px",
                                                      fontSize: "0.75rem",
                                                      paddingLeft: "0.5rem",
                                                    },
                                                    "& .MuiInputBase-root": {
                                                      borderBottom: "none",
                                                      "&::before": {
                                                        borderBottom: "none",
                                                      },
                                                      "&::after": {
                                                        borderBottom: "none",
                                                      },
                                                      "&:hover:not(.Mui-disabled):before":
                                                        {
                                                          borderBottom: "none",
                                                        },
                                                    },
                                                  }}
                                                  variant="standard"
                                                  borderColor={
                                                    theme.palette.customColors
                                                      .white
                                                  }
                                                />
                                              </Box>
                                            </StyledTableCell>
                                          </StyledTableRow>
                                        );
                                      });
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Card>
                    );
                  })
                )}
              </Box>

              {/* Inventory Pagination */}
              {/* {swagStoreStore.fetchedInventoryProducts.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 50]}
                    component="div"
                    count={swagStoreStore.inventoryTotalCount}
                    rowsPerPage={swagStoreStore.inventoryLimit}
                    page={swagStoreStore.inventoryPage - 1}
                    onPageChange={(event, newPage) => {
                      swagStoreStore.setInventoryPage(newPage + 1);
                      swagStoreStore.getPacks().catch(console.error);
                    }}
                    onRowsPerPageChange={(event) => {
                      const newLimit = parseInt(event.target.value, 10);
                      swagStoreStore.setInventoryLimit(newLimit);
                      swagStoreStore.getPacks().catch(console.error);
                    }}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      pt: 2,
                    }}
                  />
                </Box>
              )} */}
            </Box>
          )}

          {/* Swageazy Catalogue Section */}
          {swagStoreStore.selectedSource === "catalogue" && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 2,
                  mt: 4,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {(() => {
                    const allProductIds =
                      swagStoreStore.filteredCatalogueProducts.map(
                        (pack) => pack.id,
                      );
                    const allSelected =
                      allProductIds.length > 0 &&
                      allProductIds.every((id) =>
                        swagStoreStore.selectedCatalogueProducts.includes(id),
                      );
                    const someSelected =
                      allProductIds.length > 0 &&
                      allProductIds.some((id) =>
                        swagStoreStore.selectedCatalogueProducts.includes(id),
                      ) &&
                      !allSelected;

                    return (
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={
                          !userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                        }
                        onClick={(event) => handleSelectAllCatalogue(event)}
                        sx={{
                          color: theme.palette.customColors?.lightGrayBorder,
                          "&.Mui-checked": {
                            color: theme.palette.customColors?.blackBorder,
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: theme.palette.customColors?.blackBorder,
                          },
                        }}
                      />
                    );
                  })()}
                  <CustomTypography variant="heading6" fontWeight={700}>
                    Swageazy Catalogue
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: isMobile ? "100%" : "250px", flex: 1 }}>
                    <FormTextField
                      placeholder="Search catalogue..."
                      value={swagStoreStore.catalogueSearchTerm}
                      onChange={(e, value) => {
                        swagStoreStore.setCatalogueSearchTerm(value || "");
                        // Auto-load initial data when search field becomes empty
                        if (!value || value.trim() === "") {
                          swagStoreStore.setCataloguePage(1);
                          swagStoreStore.getProducts();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          swagStoreStore.setCataloguePage(1);
                          swagStoreStore.getProducts();
                        }
                      }}
                      sx={{
                        marginBottom: "0px",
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.customColors.grayBackGroundColor,
                        minHeight: "32px",
                        maxHeight: "32px",
                      }}
                      startAdornment={
                        <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                      }
                      borderColor={theme.palette.customColors.white}
                    />
                  </Box>
                  <Box sx={{ minWidth: isMobile ? "100%" : "120px" }}>
                    <AppDropDown
                      title=""
                      placeholderText="Select categories..."
                      isMultiSelect={true}
                      valuesList={categoryValuesList}
                      list={categoryList}
                      onMultiSelectionChange={(ids) => {
                        swagStoreStore.setSelectedCatalogueCategoryIds(
                          ids.map(Number),
                        );
                        // Category filtering is done locally - no API call needed
                      }}
                      fontSize="0.875rem"
                      borderColor={theme.palette.customColors.white}
                      borderRadius="8px"
                      backgroundColor={
                        theme.palette.customColors.grayBackGroundColor
                      }
                      height="32px"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Product Grid */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mb: 2,
                }}
              >
                {swagStoreStore.loadingCatalogue ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 8,
                      gap: 2,
                      minHeight: "300px",
                    }}
                  >
                    <CircularProgress
                      size={40}
                      sx={{
                        color: theme.palette.customColors?.purple,
                      }}
                    />
                    <CustomTypography
                      variant="body1"
                      color={theme.palette.text.secondary}
                    >
                      Loading catalogue products...
                    </CustomTypography>
                  </Box>
                ) : swagStoreStore.filteredCatalogueProducts.length === 0 ? (
                  <Box
                    key="no-products"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                      minHeight: "200px",
                    }}
                  >
                    <CustomTypography
                      variant="body1"
                      color={theme.palette.text.secondary}
                    >
                      No products found
                    </CustomTypography>
                  </Box>
                ) : (
                  swagStoreStore.filteredCatalogueProducts.map((pack) => {
                    const isSelected =
                      swagStoreStore.selectedCatalogueProducts?.includes(
                        pack?.id,
                      );
                    const isLimitedStock =
                      swagStoreStore.editedProductLimitedStock[pack.id] !==
                      undefined
                        ? (swagStoreStore.editedProductLimitedStock[pack.id] ??
                          false)
                        : Boolean(pack.limited_stock);
                    const packBasePriceFloor =
                      pack.pack_cost_price != null &&
                      !Number.isNaN(Number(pack.pack_cost_price))
                        ? Number(pack.pack_cost_price)
                        : undefined;
                    return (
                      <Card
                        key={pack.id}
                        sx={{
                          position: "relative",
                          p: 2,
                          cursor:
                            userStore.hasPermission(
                              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                            ) && isLimitedStock
                              ? "pointer"
                              : "default",
                          border: isSelected
                            ? `2px solid ${theme.palette.customColors?.purple}`
                            : `1px solid ${theme.palette.customColors.lightGray}`,
                          borderRadius: "12px",
                          backgroundColor: isSelected
                            ? theme.palette.customColors.blueLightBackground
                            : theme.palette.customColors.white,
                          "&:hover": userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                            ? {
                                borderColor: theme.palette.customColors?.purple,
                                backgroundColor: isSelected
                                  ? theme.palette.customColors
                                      .blueLightBackground
                                  : theme.palette.customColors
                                      .blueLightBackground,
                              }
                            : {},
                          transition: "all 0.2s ease",
                          opacity: userStore.hasPermission(
                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                          )
                            ? 1
                            : 0.6,
                        }}
                        onClick={() => {
                          if (
                            !userStore.hasPermission(
                              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                            )
                          )
                            return;
                          if (!isLimitedStock) return;
                          setExpandedProductIds((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(pack.id)) {
                              newSet.delete(pack.id);
                            } else {
                              newSet.add(pack.id);
                            }
                            return newSet;
                          });
                        }}
                      >
                        {/* Product Header Section - Horizontal Layout */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Checkbox - Left Side */}
                          <Box
                            sx={{
                              pt: 0.5,
                              marginLeft: "-8px",
                              marginRight: "-8px",
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                userStore.hasPermission(
                                  EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                )
                              ) {
                                handleToggleCatalogueProduct(pack.id);
                              }
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: theme.palette.customColors?.purple,
                                padding: "4px",
                                "&.Mui-checked": {
                                  color: theme.palette.customColors?.purple,
                                },
                              }}
                            />
                          </Box>
                          {/* Product Image - Left Side */}
                          <Box
                            sx={{
                              width: "80px",
                              height: "80px",
                              minWidth: "80px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor:
                                theme.palette.customColors.veryLightGray,
                              borderRadius: "8px",
                              overflow: "hidden",
                              p: 1,
                            }}
                          >
                            <Avatar
                              src={getPackDisplayImage(pack)}
                              alt={pack.label}
                              variant="rounded"
                              sx={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "8px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>

                          {/* Product Info - Middle Section */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            {/* Product Name and Category */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <CustomTypography
                                variant="heading6"
                                fontWeight={700}
                                style={{ fontSize: "1.125rem" }}
                              >
                                {pack.label}
                              </CustomTypography>
                              {pack.category_name && (
                                <Chip
                                  label={pack.category_name}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      theme.palette.customColors
                                        .blueLightBackground,
                                    color: theme.palette.customColors?.purple,
                                    fontSize: "0.75rem",
                                    height: "24px",
                                  }}
                                />
                              )}
                            </Box>

                            {/* Product Details */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.grey[700]}
                                fontWeight={400}
                                style={{ fontSize: "0.875rem" }}
                              >
                                Product ID: {pack.id}
                              </CustomTypography>
                            </Box>

                            {/* Summary Statistics */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mt: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <InventoryIcon
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: theme.palette.grey[600],
                                  }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[600]}
                                  fontWeight={400}
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  {pack.products?.reduce(
                                    (total, product) =>
                                      total + (product.variants?.length || 0),
                                    0,
                                  ) || 0}{" "}
                                  variants
                                </CustomTypography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {/* Limited Stock Toggle - Catalogue only */}
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={
                                        swagStoreStore
                                          .editedProductLimitedStock[
                                          pack.id
                                        ] !== undefined
                                          ? (swagStoreStore
                                              .editedProductLimitedStock[
                                              pack.id
                                            ] ?? false)
                                          : Boolean(pack.limited_stock)
                                      }
                                      onChange={(e) => {
                                        if (
                                          !userStore.hasPermission(
                                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                          )
                                        )
                                          return;
                                        const checked = e.target.checked;
                                        swagStoreStore.setEditedProductLimitedStock(
                                          pack.id,
                                          checked,
                                        );
                                        if (!checked) {
                                          setExpandedProductIds((prev) => {
                                            const newSet = new Set(prev);
                                            newSet.delete(pack.id);
                                            return newSet;
                                          });
                                        }
                                      }}
                                      disabled={
                                        !userStore.hasPermission(
                                          EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                        )
                                      }
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor:
                                              theme.palette.customColors
                                                ?.purple,
                                          },
                                      }}
                                    />
                                  }
                                  label={
                                    <CustomTypography
                                      variant="body3"
                                      color={theme.palette.grey[700]}
                                      fontWeight={500}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      Set Stock Limit
                                    </CustomTypography>
                                  }
                                  sx={{ mt: 0.5, mr: 0 }}
                                />

                                {/* <TrendingUpIcon
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: theme.palette.grey[600],
                                  }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[600]}
                                  fontWeight={400}
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  {pack.products?.reduce((total, product) => {
                                    return (
                                      total +
                                      (product.variants?.reduce((sum, v) => {
                                        const initialQty =
                                          v.initial_quantity ?? 0;
                                        const qtyForStore =
                                          v.quantity_for_store ?? 0;
                                        const usedQty = v.used_quantity ?? 0;
                                        return (
                                          sum +
                                          (initialQty - (qtyForStore + usedQty))
                                        );
                                      }, 0) ?? 0)
                                    );
                                  }, 0) || 0}{" "}
                                  in stock
                                </CustomTypography> */}
                              </Box>
                            </Box>
                          </Box>

                          {/* Price and Selection - Right Side */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 1,
                              minWidth: "120px",
                              mt: 0.5,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Price */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.25,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <FormTextField
                                  type="number"
                                  disabled={
                                    !userStore.hasPermission(
                                      EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                    )
                                  }
                                  value={
                                    swagStoreStore.editedProductPrices[
                                      pack.id
                                    ] !== undefined
                                      ? swagStoreStore.editedProductPrices[
                                          pack.id
                                        ] === null
                                        ? ""
                                        : (swagStoreStore.editedProductPrices[
                                            pack.id
                                          ] ?? "")
                                      : (pack.pack_price ?? "")
                                  }
                                  onChange={(e, value) => {
                                    if (
                                      !userStore.hasPermission(
                                        EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                      )
                                    )
                                      return;
                                    if (
                                      value === "" ||
                                      value === undefined ||
                                      value === null
                                    ) {
                                      swagStoreStore.setEditedProductPrice(
                                        pack.id,
                                        null,
                                      );
                                    } else {
                                      const price = Number(value);
                                      swagStoreStore.setEditedProductPrice(
                                        pack.id,
                                        isNaN(price) ? null : price,
                                      );
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (
                                      !userStore.hasPermission(
                                        EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                      ) ||
                                      packBasePriceFloor === undefined
                                    ) {
                                      return;
                                    }
                                    const raw = e.target.value;
                                    if (raw === "") return;
                                    const price = Number(raw);
                                    if (
                                      isNaN(price) ||
                                      price >= packBasePriceFloor
                                    ) {
                                      return;
                                    }
                                    swagStoreStore.setEditedProductPrice(
                                      pack.id,
                                      packBasePriceFloor,
                                    );
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  sx={{
                                    width: "80px",
                                    height: "32px",
                                    marginBottom: "0px",
                                    backgroundColor:
                                      theme.palette.customColors.lightGreyColor,
                                    "& .MuiInputBase-input": {
                                      paddingTop: "4px",
                                      paddingBottom: "4px",
                                      fontSize: "0.9rem",
                                      fontWeight: 600,
                                      textAlign: "left",
                                    },
                                    "& .MuiInputBase-root": {
                                      borderBottom: "none",
                                      "&::before": {
                                        borderBottom: "none",
                                      },
                                      "&::after": {
                                        borderBottom: "none",
                                      },
                                      "&:hover:not(.Mui-disabled):before": {
                                        borderBottom: "none",
                                      },
                                    },
                                  }}
                                  variant="standard"
                                  borderColor={theme.palette.customColors.white}
                                  startAdornment={
                                    <CurrencyRupeeIcon
                                      sx={{
                                        color: theme.palette.grey[500],
                                        height: "16px",
                                        width: "16px",
                                        marginLeft: "8px",
                                      }}
                                    />
                                  }
                                />
                                {/* Accordion Icon - only when Limit stock is on */}
                                {isLimitedStock && (
                                  <IconButton
                                    size="small"
                                    sx={{
                                      padding: 0,
                                      color: theme.palette.grey[600],
                                      transform: expandedProductIds.has(pack.id)
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.3s ease",
                                    }}
                                  >
                                    <ExpandMoreIcon />
                                  </IconButton>
                                )}
                              </Box>
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.grey[600]}
                                fontWeight={400}
                                sx={{ fontSize: "0.7rem", marginLeft: "8px" }}
                              >
                                Selling Price
                              </CustomTypography>
                            </Box>

                            {/* Units Selected - visible only when Limit stock is on */}
                            {isLimitedStock && (
                              <Chip
                                label={`${
                                  pack.products?.reduce((total, product) => {
                                    return (
                                      total +
                                      (product.variants?.reduce((sum, v) => {
                                        return (
                                          sum + (v.quantity_for_store ?? 0)
                                        );
                                      }, 0) ?? 0)
                                    );
                                  }, 0) || 0
                                } units selected`}
                                size="small"
                                sx={{
                                  backgroundColor: "#E8F5E9",
                                  color: "#2E7D32",
                                  fontSize: "0.75rem",
                                  height: "24px",
                                  fontWeight: 500,
                                  paddingLeft: "10px",
                                  paddingRight: "10px",
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Variant Quantities Accordion - only when Limit stock is on */}
                        {pack.products?.some(
                          (product) =>
                            product.variants && product.variants.length > 0,
                        ) &&
                          isLimitedStock && (
                            <Accordion
                              expanded={expandedProductIds.has(pack.id)}
                              sx={{
                                mt: 1,
                                boxShadow: "none",
                                border: "none",
                                backgroundColor: "transparent",
                                "&:before": {
                                  display: "none",
                                },
                                "&.Mui-expanded": {
                                  margin: "8px 0",
                                },
                              }}
                            >
                              <AccordionSummary
                                sx={{
                                  px: 0,
                                  py: 0,
                                  minHeight: "auto",
                                  display: "none",
                                }}
                              />
                              <AccordionDetails
                                sx={{
                                  px: 0,
                                  pb: 0,
                                  pt: 0,
                                  backgroundColor: "transparent",
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.customColors.blackBorder}
                                  fontWeight={700}
                                  style={{
                                    fontSize: "1.125rem",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Select Variants to Add
                                </CustomTypography>
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.grey[600]}
                                  fontWeight={400}
                                  style={{
                                    fontSize: "0.75rem",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Choose quantity for each variant you want to
                                  list
                                </CustomTypography>
                                <TableContainer
                                  component={Paper}
                                  sx={{
                                    boxShadow: "none",
                                    border: `1px solid ${theme.palette.customColors.lightGray}`,
                                    borderRadius: "8px",
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow
                                        sx={{
                                          backgroundColor:
                                            theme.palette.customColors
                                              .cardBackground,
                                        }}
                                      >
                                        <AppTableHeaderCell
                                          title="SKU"
                                          fontSize="12px"
                                          fontColor={theme.palette.grey[700]}
                                          fontWeight={700}
                                          txtAlign="left"
                                          sx={{
                                            padding: "8px 12px",
                                            minWidth: "auto",
                                          }}
                                        />
                                        <AppTableHeaderCell
                                          title="SIZE"
                                          fontSize="12px"
                                          fontColor={theme.palette.grey[700]}
                                          fontWeight={700}
                                          txtAlign="left"
                                          sx={{
                                            padding: "8px 12px",
                                            minWidth: "auto",
                                          }}
                                        />
                                        {/* <AppTableHeaderCell
                                          title="AVAILABLE"
                                          fontSize="12px"
                                          fontColor={theme.palette.grey[700]}
                                          fontWeight={700}
                                          txtAlign="left"
                                          sx={{
                                            padding: "8px 12px",
                                            minWidth: "auto",
                                          }}
                                        /> */}
                                        <AppTableHeaderCell
                                          title="QTY ON STORE"
                                          fontSize="12px"
                                          fontColor={theme.palette.grey[700]}
                                          fontWeight={700}
                                          txtAlign="left"
                                          sx={{
                                            padding: "8px 12px",
                                            minWidth: "auto",
                                          }}
                                        />
                                        <AppTableHeaderCell
                                          title="QTY TO LIST"
                                          fontSize="12px"
                                          fontColor={theme.palette.grey[700]}
                                          fontWeight={700}
                                          txtAlign="right"
                                          sx={{
                                            padding: "8px 12px",
                                            minWidth: "auto",
                                          }}
                                        />
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {pack.products?.map((product) => {
                                        if (
                                          !product.variants ||
                                          product.variants.length === 0
                                        ) {
                                          return null;
                                        }
                                        return product.variants.map(
                                          (variant) => {
                                            const vid =
                                              variant.variant_id ?? variant.id;
                                            if (vid == null) return null;

                                            // Use original quantity_for_store (not edited values) - will update after API success
                                            const currentQtyForStore =
                                              variant.quantity_for_store ?? 0;

                                            return (
                                              <StyledTableRow key={vid}>
                                                <StyledTableCell
                                                  sx={{
                                                    fontSize: "0.875rem",
                                                    padding: "8px 12px",
                                                    fontWeight: 400,
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <InventoryBoxIcon
                                                      sx={{
                                                        fontSize: "0.875rem",
                                                        color:
                                                          theme.palette
                                                            .grey[600],
                                                      }}
                                                    />
                                                    {variant.sku ||
                                                      variant.client_sku ||
                                                      "-"}
                                                  </Box>
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  sx={{
                                                    fontSize: "0.875rem",
                                                    padding: "8px 12px",
                                                    fontWeight: 400,
                                                  }}
                                                >
                                                  {variant.size || "-"}
                                                </StyledTableCell>
                                                {/* <StyledTableCell
                                                sx={{
                                                  padding: "8px 12px",
                                                  fontWeight: 400,
                                                }}
                                              >
                                                <Chip
                                                  label={`${availableQty} units`}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: "#E8F5E9",
                                                    color: "#2E7D32",
                                                    fontSize: "0.75rem",
                                                    height: "24px",
                                                    fontWeight: 500,
                                                    paddingLeft: "10px",
                                                    paddingRight: "10px",
                                                  }}
                                                />
                                              </StyledTableCell> */}
                                                <StyledTableCell
                                                  sx={{
                                                    padding: "8px 12px",
                                                    fontWeight: 400,
                                                    textAlign: "left",
                                                    fontSize: "0.875rem",
                                                    paddingLeft: "2rem",
                                                  }}
                                                >
                                                  {currentQtyForStore}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  sx={{
                                                    padding: "8px 12px",
                                                    fontWeight: 400,
                                                    textAlign: "right",
                                                  }}
                                                  onMouseDown={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <Box
                                                    onMouseDown={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                  >
                                                    <FormTextField
                                                      type="number"
                                                      disabled={
                                                        !userStore.hasPermission(
                                                          EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                                        )
                                                      }
                                                      value={
                                                        swagStoreStore
                                                          .editedProductVariantQuantities[
                                                          pack.id
                                                        ]?.[vid] !== undefined
                                                          ? swagStoreStore
                                                              .editedProductVariantQuantities[
                                                              pack.id
                                                            ][vid] === null
                                                            ? ""
                                                            : (swagStoreStore
                                                                .editedProductVariantQuantities[
                                                                pack.id
                                                              ][vid] ?? "")
                                                          : ""
                                                      }
                                                      onChange={(e, value) => {
                                                        if (
                                                          !userStore.hasPermission(
                                                            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                                          )
                                                        )
                                                          return;
                                                        if (
                                                          value === "" ||
                                                          value === undefined ||
                                                          value === null
                                                        ) {
                                                          swagStoreStore.setEditedProductVariantQuantity(
                                                            pack.id,
                                                            vid,
                                                            null,
                                                          );
                                                        } else {
                                                          const quantity =
                                                            Number(value);
                                                          swagStoreStore.setEditedProductVariantQuantity(
                                                            pack.id,
                                                            vid,
                                                            isNaN(quantity)
                                                              ? null
                                                              : quantity,
                                                          );
                                                        }
                                                      }}
                                                      onKeyDown={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      sx={{
                                                        width: "80px",
                                                        height: "28px",
                                                        marginBottom: "0px",
                                                        backgroundColor:
                                                          theme.palette
                                                            .customColors
                                                            .lightGreyColor,
                                                        "& .MuiInputBase-input":
                                                          {
                                                            paddingTop: "4px",
                                                            paddingBottom:
                                                              "4px",
                                                            fontSize:
                                                              "0.875rem",
                                                            fontWeight: 400,
                                                            textAlign: "center",
                                                          },
                                                        "& .MuiInputBase-root":
                                                          {
                                                            borderBottom:
                                                              "none",
                                                            "&::before": {
                                                              borderBottom:
                                                                "none",
                                                            },
                                                            "&::after": {
                                                              borderBottom:
                                                                "none",
                                                            },
                                                            "&:hover:not(.Mui-disabled):before":
                                                              {
                                                                borderBottom:
                                                                  "none",
                                                              },
                                                          },
                                                      }}
                                                      variant="standard"
                                                      borderColor={
                                                        theme.palette
                                                          .customColors.white
                                                      }
                                                    />
                                                  </Box>
                                                </StyledTableCell>
                                              </StyledTableRow>
                                            );
                                          },
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          )}
                      </Card>
                    );
                  })
                )}
              </Box>

              {/* Catalogue Pagination */}
              {/* {swagStoreStore.fetchedCatalogueProducts.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 50]}
                    component="div"
                    count={swagStoreStore.catalogueTotalCount}
                    rowsPerPage={swagStoreStore.catalogueLimit}
                    page={swagStoreStore.cataloguePage - 1}
                    onPageChange={(event, newPage) => {
                      swagStoreStore.setCataloguePage(newPage + 1);
                      swagStoreStore.getProducts().catch(console.error);
                    }}
                    onRowsPerPageChange={(event) => {
                      const newLimit = parseInt(event.target.value, 10);
                      swagStoreStore.setCatalogueLimit(newLimit);
                      swagStoreStore.getProducts().catch(console.error);
                    }}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      pt: 2,
                    }}
                  />
                </Box>
              )} */}
            </Box>
          )}

          {/* Upload via CSV Template Section */}
          {swagStoreStore.selectedSource === "csv" && (
            <Box>
              <Box sx={{ mt: 4, mb: 0 }}>
                <CustomTypography variant="heading6" fontWeight={700}>
                  Upload via CSV Template
                </CustomTypography>
                <CustomTypography
                  variant="body1"
                  color={theme.palette.text.secondary}
                  style={{ marginTop: 10 }}
                >
                  Download the template first, edit it with the products you
                  want to upload, and remove any items you don't need before
                  uploading your CSV file.
                </CustomTypography>

                {/* Download Buttons */}
                <Box
                  sx={{
                    display: { xs: "block", sm: "block", md: "flex" },
                    alignItems: "center",
                    gap: 5,
                    mb: 3,
                    mt: 4,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <AppButton
                      label="Download Customer Inventory Template"
                      onClick={() => handleDownloadTemplate("inventory")}
                      variant="grayscale"
                      fontWeight={400}
                      fontSize="14px"
                      customStyles={{
                        width: "100%",
                        textTransform: "none",
                        border: `1px solid ${theme.palette.customColors.darkBlackBorder}`,
                        backgroundColor: theme.palette.customColors.white,
                        color: theme.palette.customColors.darkBlackBorder,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.veryLightGray,
                          color: theme.palette.customColors.darkBlackBorder,

                          borderColor:
                            theme.palette.customColors.darkBlackBorder,
                        },
                        minHeight: "50px",
                        maxHeight: "50px",
                        pl: 4,
                        pr: 4,
                      }}
                      startIcon={<DownloadIcon />}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <AppButton
                      label="Download Swageazy Catalogue Template"
                      onClick={() => handleDownloadTemplate("catalogue")}
                      variant="grayscale"
                      fontWeight={400}
                      fontSize="14px"
                      customStyles={{
                        width: "100%",
                        textTransform: "none",
                        border: `1px solid ${theme.palette.customColors.EmeraldGreen}`,
                        backgroundColor: theme.palette.customColors.white,
                        color: theme.palette.customColors.EmeraldGreen,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.EmeraldGreen,
                          color: theme.palette.customColors.white,
                          borderColor: theme.palette.customColors.EmeraldGreen,
                        },
                        minHeight: "50px",
                        maxHeight: "50px",
                        pl: 4,
                        pr: 4,
                        mt: { xs: 2, sm: 2, md: 0 },
                      }}
                      startIcon={<DownloadIcon />}
                    />
                  </Box>
                </Box>
                {/* Drag and Drop Area */}
                <Box>
                  <ImageUpload
                    size="large"
                    previewImage={false}
                    showImagesList={false}
                    uploadText="Click to upload or drag and drop CSV file"
                    addImagesToBeUploaded={(files: File[]) => {
                      if (files.length > 0) {
                        handleCsvFileSelect(files[0]);
                      }
                    }}
                    disabled={false}
                    allowedFileTypes="text/csv"
                    fileInfo={false}
                    uploadIcon={<CloudUploadIcon />}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box>
            <CustomTypography
              variant="body2"
              color={theme.palette.text.secondary}
            >
              {swagStoreStore.selectedSource === "csv"
                ? csvFile
                  ? "CSV file ready to upload"
                  : "No file selected"
                : `${getSelectedProductsCount()} product${
                    getSelectedProductsCount() !== 1 ? "s" : ""
                  } selected`}
            </CustomTypography>
          </Box>

          <AppButton
            label={
              swagStoreStore.selectedSource === "csv"
                ? "Upload CSV"
                : "Add Selected Products"
            }
            onClick={handleAddSelectedProducts}
            variant="primaryBlock"
            disabled={
              !userStore.hasPermission(
                EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
              ) || (swagStoreStore.selectedSource === "csv" ? !csvFile : false)
              // : getSelectedProductsCount() === 0
            }
            customStyles={{
              paddingLeft: "30px",
              paddingRight: "30px",
              textTransform: "none",
              backgroundColor: theme.palette.customColors?.purple,
              "&:hover": {
                backgroundColor: theme.palette.customColors?.purpleDark,
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors?.greyBorder,
              },
              minHeight: "40px",
              maxHeight: "40px",
            }}
          />
        </Box>
      </Dialog>
    );
  },
);

export default AddProductsModal;
